import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Menu, MenuItem, SubMenu } from '@szhsin/react-menu';
import cx from 'classnames';

import { ALL } from '../../../../../../constants/accessToPages';
import { pendoTrack } from '../../../../../../pendo/PendoUtils';
import { NSL_NAVBAR } from '../../../../../../services/i18n/namespaces';
import { isActive, NAV_MENU, setDefaultFilterItemsToStorage, withoutSpaces } from '../MenuOptionsService';

export default forwardRef(function MenuList({ className, menuItems, user, active, maxVisibleItemsCount, useIds }, ref) {
  const { t } = useTranslation(NSL_NAVBAR, { keyPrefix: 'menu' });

  const mainMenuItems = useMemo(
    function() {
      const [mainMenuItems, moreMenuItems] = menuItems.reduce(
        (a, m, i) => {
          a[i < maxVisibleItemsCount ? 0 : 1].push({ ...m });
          return a;
        },
        [[], []]
      );

      if (moreMenuItems.length > 0) {
        mainMenuItems.push({
          i18nKey: maxVisibleItemsCount > 0 ? 'more' : 'menu',
          path: '',
          access: ALL,
          subItems: moreMenuItems
        });
      }
      return mainMenuItems;
    },
    [maxVisibleItemsCount, menuItems]
  );

  return (
    <ul
      className={cx('nav-items', {
        'invisible-ref-container': !!ref
      })}
      ref={ref}
    >
      {mainMenuItems.map(menuItem => {
        let i18nKey = menuItem.i18nKey;
        if (i18nKey === 'menu') {
          i18nKey = 'self';
        } else if (i18nKey !== 'more') {
          i18nKey = `${menuItem.i18nKey}.self`;
        }
        const id = withoutSpaces(`${NAV_MENU}_${menuItem.i18nKey}`);
        const restProps = useIds ? { 'data-id': id } : {};
        if (menuItem.subItems) {
          return (
            <Menu
              key={menuItem.i18nKey}
              menuButton={
                <li className={cx('eui-nav-item menu', { active: isActive(menuItem, active) })} {...restProps}>
                  {t(i18nKey)}
                </li>
              }
            >
              {menuItem.subItems.map(function(subItem) {
                return dropdownMenuItem(subItem, user, active, useIds, menuItem.i18nKey);
              })}
            </Menu>
          );
        } else {
          const name = t(menuItem.i18nKey);
          return (
            <li className={cx('eui-nav-item', { active: menuItem.path === active })} key={menuItem.i18nKey}>
              <NavLink
                {...restProps}
                className="eui-nav-link empty"
                onClick={() => {
                  pendoTrack('Changed location by menu click', {
                    pathName: menuItem.path,
                    buttonName: name
                  });
                  setDefaultFilterItemsToStorage();
                }}
                to={menuItem.path}
              >
                {name}
              </NavLink>
            </li>
          );
        }
      })}
    </ul>
  );

  function dropdownMenuItem(item, user, active, useIds, parentI18nKey) {
    let nestedId;
    let i18nKey = item.i18nKey;
    if (['more', 'menu'].includes(parentI18nKey)) {
      nestedId = withoutSpaces(`${NAV_MENU}_${item.i18nKey}`);
    } else {
      nestedId = withoutSpaces(`${NAV_MENU}_${parentI18nKey}_${item.i18nKey}`);
      i18nKey = `${parentI18nKey}.${item.i18nKey}`;
    }
    const restProps = useIds ? { 'data-id': nestedId } : {};

    if (!item.subItems) {
      const menuItemName = t(i18nKey);
      const props = {};
      if (item.onClick) {
        props.onClick = () => {
          pendoTrack('Changed location by menu click', { url: item.path, menuItemName });
          item.onClick();
        };
      }
      return (
        <MenuItem key={item.path} {...restProps}>
          <NavLink className="eui-nav-link" to={item.path} {...props}>
            {menuItemName}
          </NavLink>
        </MenuItem>
      );
    }
    return (
      <SubMenu
        label={<span {...restProps}>{t(`${i18nKey}.self`)}</span>}
        itemClassName={cx({ active: isActive(item, active) })}
        key={item.i18nKey}
      >
        {item.subItems.map(function(subItem) {
          return dropdownMenuItem(subItem, user, active, true, i18nKey);
        })}
      </SubMenu>
    );
  }
});
